import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Activity, Send, Loader2, GitBranch, Gauge, Zap, Sparkles } from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState } from "@/components/common";
import { useApp } from "@/lib/store";
import { shortId } from "@/lib/utils";

interface Quality {
  volume: number; freshness_seconds: number | null; completeness: number;
  distinct_subjects: number; distinct_verbs: number;
}
interface EventRow {
  id: string; type_key: string; subject_kind: string; subject_id: string;
  verb: string; object_kind?: string; object_id?: string; occurred_at: string;
}
interface Edge {
  id: string; subject_kind: string; subject_id: string; verb: string;
  object_kind: string; object_id: string; weight: number;
}
interface Feature {
  id: string; subject_kind: string; subject_id: string; name: string;
  value: number; window: string;
}
interface Trigger {
  id: string; name: string; type_key: string; target_kind: string;
}

export function Events() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();

  const quality = useQuery<Quality>({
    queryKey: ["event-quality", ws],
    queryFn: () => rawFetch(`/events/events/quality/?workspace=${ws}`),
    enabled: !!ws,
  });
  const events = useQuery<{ results: EventRow[] }>({
    queryKey: ["events", ws],
    queryFn: () => rawFetch(`/events/events/?page_size=20&ordering=-occurred_at${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const edges = useQuery<{ results: Edge[] }>({
    queryKey: ["behavior-edges", ws],
    queryFn: () => rawFetch(`/events/behavior-edges/?page_size=20${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const features = useQuery<{ results: Feature[] }>({
    queryKey: ["features", ws],
    queryFn: () => rawFetch(`/events/features/?page_size=20${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });
  const triggers = useQuery<{ results: Trigger[] }>({
    queryKey: ["triggers", ws],
    queryFn: () => rawFetch(`/events/triggers/?page_size=20${ws ? `&workspace=${ws}` : ""}`),
    enabled: !!ws,
  });

  if (!ws) {
    return (
      <>
        <PageHeader title="Events" subtitle="The behavioral layer — turn signals into features and triggers." />
        <EmptyState title="Select a workspace first." />
      </>
    );
  }

  const q = quality.data;
  const fresh = q?.freshness_seconds == null ? "—"
    : q.freshness_seconds < 60 ? `${q.freshness_seconds.toFixed(0)}s`
    : q.freshness_seconds < 3600 ? `${(q.freshness_seconds / 60).toFixed(0)}m`
    : `${(q.freshness_seconds / 3600).toFixed(1)}h`;

  return (
    <>
      <PageHeader
        title="Events"
        subtitle="Behavioral signals → behavior graph → features → triggers → closed-loop actions."
        help="Ingested events project a behavior graph (who did what to whom), compute per-subject features, and can fire triggers that launch agents or request governed actions — the loop that turns knowledge into action."
        learnMore={{ to: "/howto", label: "Platform how-to" }}
      />

      {/* Quality metrics */}
      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5">
        <Metric Icon={Activity} label="Volume" value={q?.volume ?? "—"} />
        <Metric Icon={Gauge} label="Freshness" value={fresh} />
        <Metric Icon={Sparkles} label="Completeness" value={q ? `${(q.completeness * 100).toFixed(0)}%` : "—"} />
        <Metric Icon={GitBranch} label="Subjects" value={q?.distinct_subjects ?? "—"} />
        <Metric Icon={Zap} label="Verbs" value={q?.distinct_verbs ?? "—"} />
      </div>

      <IngestForm ws={ws} onDone={() => {
        qc.invalidateQueries({ queryKey: ["events", ws] });
        qc.invalidateQueries({ queryKey: ["event-quality", ws] });
        qc.invalidateQueries({ queryKey: ["behavior-edges", ws] });
        qc.invalidateQueries({ queryKey: ["features", ws] });
      }} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Recent events" error={events.error}>
          {(events.data?.results?.length ?? 0) === 0 ? <EmptyState title="No events yet." /> : (
            <ul className="divide-y divide-border text-sm">
              {events.data!.results.map((e) => (
                <li key={e.id} className="flex items-center justify-between py-1.5">
                  <span className="truncate">
                    <code className="text-xs text-accent">{e.subject_kind}:{shortId(e.subject_id)}</code>
                    <span className="mx-1 text-muted">{e.verb}</span>
                    {e.object_kind && <code className="text-xs text-muted">{e.object_kind}:{shortId(e.object_id)}</code>}
                  </span>
                  <code className="ml-2 shrink-0 text-[11px] text-muted">{e.type_key}</code>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Behavior graph (edges)" error={edges.error}>
          {(edges.data?.results?.length ?? 0) === 0 ? <EmptyState title="No edges yet." /> : (
            <ul className="divide-y divide-border text-sm">
              {edges.data!.results.map((e) => (
                <li key={e.id} className="flex items-center justify-between py-1.5">
                  <span className="truncate">
                    <code className="text-xs text-accent">{e.subject_kind}:{shortId(e.subject_id)}</code>
                    <span className="mx-1 text-muted">{e.verb}</span>
                    <code className="text-xs text-muted">{e.object_kind}:{shortId(e.object_id)}</code>
                  </span>
                  <span className="pill ml-2 shrink-0 text-[10px]">×{e.weight}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Feature store" error={features.error}>
          {(features.data?.results?.length ?? 0) === 0 ? <EmptyState title="No features yet." /> : (
            <ul className="divide-y divide-border text-sm">
              {features.data!.results.map((f) => (
                <li key={f.id} className="flex items-center justify-between py-1.5">
                  <span className="truncate">
                    <code className="text-xs text-accent">{f.subject_kind}:{shortId(f.subject_id)}</code>
                    <span className="ml-1 text-muted">{f.name}</span>
                    <span className="ml-1 text-[11px] text-muted">[{f.window}]</span>
                  </span>
                  <span className="ml-2 shrink-0 font-mono text-xs">{f.value}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Triggers" error={triggers.error}>
          {(triggers.data?.results?.length ?? 0) === 0 ? <EmptyState title="No triggers configured." hint="Triggers fire agents or actions on matching events." /> : (
            <ul className="divide-y divide-border text-sm">
              {triggers.data!.results.map((t) => (
                <li key={t.id} className="flex items-center justify-between py-1.5">
                  <span className="truncate">{t.name}</span>
                  <span className="flex items-center gap-2">
                    <code className="text-[11px] text-muted">{t.type_key}</code>
                    <span className="pill text-[10px]">{t.target_kind}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}

function Metric({ Icon, label, value }: { Icon: typeof Activity; label: string; value: React.ReactNode }) {
  return (
    <div className="card">
      <div className="flex items-center gap-1.5 text-xs text-muted">
        <Icon size={13} /> {label}
      </div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}

function Panel({ title, error, children }: { title: string; error?: unknown; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      {error ? <ErrorBox error={error} /> : children}
    </div>
  );
}

function IngestForm({ ws, onDone }: { ws: string; onDone: () => void }) {
  const [typeKey, setTypeKey] = useState("order.placed");
  const [subjectKind, setSubjectKind] = useState("customer");
  const [subjectId, setSubjectId] = useState("");
  const [verb, setVerb] = useState("placed");

  const ingest = useMutation({
    mutationFn: () => rawFetch(`/events/ingest/`, {
      method: "POST",
      body: JSON.stringify({
        workspace: ws, type_key: typeKey,
        subject_kind: subjectKind, subject_id: subjectId, verb,
        occurred_at: new Date().toISOString(),
        properties: {},
      }),
    }),
    onSuccess: () => { setSubjectId(""); onDone(); },
  });

  return (
    <div className="card mb-4">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <input className="input" placeholder="type_key" value={typeKey} onChange={(e) => setTypeKey(e.target.value)} />
        <input className="input" placeholder="subject_kind" value={subjectKind} onChange={(e) => setSubjectKind(e.target.value)} />
        <input className="input" placeholder="subject_id" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} />
        <input className="input" placeholder="verb" value={verb} onChange={(e) => setVerb(e.target.value)} />
        <button className="btn-primary" disabled={!subjectId || ingest.isPending} onClick={() => ingest.mutate()}>
          {ingest.isPending ? <Loader2 className="mr-1 inline animate-spin" size={14} /> : <Send className="mr-1 inline" size={14} />}
          Ingest
        </button>
      </div>
      {ingest.isError && <div className="mt-2"><ErrorBox error={ingest.error} /></div>}
    </div>
  );
}
